import './PerCoachCommentsSection.css';

import React, { useState } from 'react';

import { Timestamp } from 'firebase/firestore';
import TextareaAutosize from 'react-textarea-autosize';

import {
  PerCoachCommentEntry,
  PerCoachCommentsValue,
} from '../../../types/athlete';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import PencilIcon from '../PencilIcon';
import TrashIcon from '../TrashIcon';

interface PerCoachCommentsSectionProps {
  comments: PerCoachCommentsValue;
  /** When provided the component renders an input for adding new comments. Omit for read-only. */
  onAddComment?: (text: string) => Promise<void>;
  /** UID of the currently logged-in scoring coach; enables edit/delete on their own comments. */
  currentCoachUid?: string;
  /** Display name of the current coach — used as a fallback ownership check alongside uid. */
  currentCoachName?: string;
  onEditComment?: (commentId: string, newText: string) => Promise<void>;
  onDeleteComment?: (commentId: string) => Promise<void>;
}

function formatTimestamp(ts: Timestamp): string {
  const date = ts.toDate();
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const PerCoachCommentsSection: React.FC<PerCoachCommentsSectionProps> = ({
  comments,
  onAddComment,
  currentCoachUid,
  currentCoachName,
  onEditComment,
  onDeleteComment,
}) => {
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [deletePendingId, setDeletePendingId] = useState<string | null>(null);

  const sorted = [...comments].sort(
    (a, b) => b.createdAt.toMillis() - a.createdAt.toMillis(),
  );

  const handleSave = async (): Promise<void> => {
    if (!onAddComment || !draft.trim() || saving) return;
    try {
      setSaving(true);
      await onAddComment(draft.trim());
      setDraft('');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      void handleSave();
    }
  };

  const startEdit = (entry: PerCoachCommentEntry): void => {
    setEditingId(entry.id);
    setEditDraft(entry.text);
  };

  const cancelEdit = (): void => {
    setEditingId(null);
    setEditDraft('');
  };

  const handleEditSave = async (commentId: string): Promise<void> => {
    if (!onEditComment || !editDraft.trim() || editSaving) return;
    try {
      setEditSaving(true);
      await onEditComment(commentId, editDraft.trim());
      setEditingId(null);
      setEditDraft('');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!onDeleteComment || deletePendingId == null) return;
    const commentId = deletePendingId;
    setDeletePendingId(null);
    if (editingId === commentId) {
      cancelEdit();
    }
    await onDeleteComment(commentId);
  };

  return (
    <div className="per-coach-comments-section">
      {onAddComment ? (
        <div className="per-coach-comments-input-area">
          <TextareaAutosize
            className="per-coach-comments-textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add a comment…"
            minRows={2}
            disabled={saving}
          />
          <div className="per-coach-comments-input-actions">
            <button
              type="button"
              className="per-coach-comments-save-btn"
              onClick={() => void handleSave()}
              disabled={saving || !draft.trim()}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : null}

      {sorted.length === 0 ? (
        <p className="per-coach-comments-empty">No comments yet.</p>
      ) : (
        <div className="per-coach-comments-list">
          {sorted.map((entry: PerCoachCommentEntry) => {
            const isOwner =
              (currentCoachUid != null && entry.coachUid === currentCoachUid) ||
              (currentCoachName != null && entry.coachName === currentCoachName);
            const isEditing = editingId === entry.id;

            return (
              <div key={entry.id} className="per-coach-comments-entry">
                {isEditing ? (
                  <>
                    <TextareaAutosize
                      className="per-coach-comments-textarea per-coach-comments-edit-textarea"
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      minRows={2}
                      disabled={editSaving}
                      autoFocus
                    />
                    <div className="per-coach-comments-edit-actions">
                      <button
                        type="button"
                        className="per-coach-comments-cancel-btn"
                        onClick={cancelEdit}
                        disabled={editSaving}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="per-coach-comments-save-btn"
                        onClick={() => void handleEditSave(entry.id)}
                        disabled={editSaving || !editDraft.trim()}
                      >
                        {editSaving ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="per-coach-comments-entry-text">{entry.text}</p>
                )}
                <div className="per-coach-comments-entry-meta-row">
                  <p className="per-coach-comments-entry-meta">
                    {entry.coachName} · {formatTimestamp(entry.createdAt)}
                  </p>
                  {isOwner && !isEditing && onEditComment && onDeleteComment && (
                    <div className="per-coach-comments-entry-actions">
                      <button
                        type="button"
                        className="per-coach-comments-icon-btn"
                        aria-label="Edit comment"
                        onClick={() => startEdit(entry)}
                      >
                        <PencilIcon className="per-coach-comments-icon" />
                      </button>
                      <button
                        type="button"
                        className="per-coach-comments-icon-btn per-coach-comments-icon-btn--delete"
                        aria-label="Delete comment"
                        onClick={() => setDeletePendingId(entry.id)}
                      >
                        <TrashIcon className="per-coach-comments-icon" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={deletePendingId != null}
        title="Delete comment?"
        message="Are you sure you want to delete this comment?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onCancel={() => setDeletePendingId(null)}
        onConfirm={() => void handleDeleteConfirm()}
      />
    </div>
  );
};

export default PerCoachCommentsSection;
